// ============================================================================
// CORPORATE HOOKS
// ============================================================================
// React hooks for Corporate API operations

import { useState, useEffect, useCallback } from 'react';
import { corporateService } from 'api/services/corporateService';
import type {
  Corporate,
  CreateCorporateRequest,
  UpdateCorporateRequest,
  CorporateSearchRequest,
  ID,
} from 'api/types';

/**
 * Hook to fetch all corporates (paginated)
 */
export const useCorporates = (params?: CorporateSearchRequest) => {
  const [data, setData] = useState<Corporate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchCorporates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await corporateService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchCorporates();
  }, [fetchCorporates]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchCorporates,
  };
};

/**
 * Hook to fetch a single corporate by ID
 */
export const useCorporate = (id: ID) => {
  const [data, setData] = useState<Corporate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCorporate = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await corporateService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCorporate();
  }, [fetchCorporate]);

  return {
    data,
    loading,
    error,
    refetch: fetchCorporate,
  };
};

/**
 * Hook to create a new corporate
 */
export const useCreateCorporate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createCorporate = async (
    data: CreateCorporateRequest,
    options?: {
      onSuccess?: (corporate: Corporate) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await corporateService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createCorporate,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update a corporate
 */
export const useUpdateCorporate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateCorporate = async (
    id: ID,
    data: UpdateCorporateRequest,
    options?: {
      onSuccess?: (corporate: Corporate) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await corporateService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateCorporate,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete a corporate
 */
export const useDeleteCorporate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteCorporate = async (
    id: ID,
    rowVersion: number,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await corporateService.delete(id, rowVersion);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteCorporate,
    loading,
    error,
    success,
  };
};
